import { useState } from 'react';
import { DisclosureRegion, Icon } from '../common';
import { MONTHS_ABBR } from '../../lib/dates';
import type { BookingDetail } from './types';
import { whatsappTo } from './whatsappTo';

// THE MESSAGE SLOT (Freddi, 2026-10-11: "a standardised position for all
// cards in the self service section that has a ready made message that
// represents the state of the booking/deposit etc so we can just copy and
// paste … a standard clean, neat and perfectly positioned spot … that changes
// dynamically depending on the situation").
//
// One block, one shape, one place on every self-service card: the eyebrow
// naming who it is for, the words Patricia would send next in her own
// register, Copy message, and Open WhatsApp to the household's number with
// the words already in it. The words are the card's business — the request
// card says the booking's, the Money card the money's — and change with the
// state; the spot never moves.

export const euro = (n: number) => (Number.isInteger(n) ? `€${n}` : `€${n.toFixed(2)}`);
export const day = (iso: string) => { const d = new Date(iso); return `${d.getUTCDate()} ${MONTHS_ABBR[d.getUTCMonth()]}`; };
export const dates = (b: Pick<BookingDetail, 'checkIn' | 'checkOut'>) => `${day(b.checkIn)} – ${day(b.checkOut)}`;
/** "Bracken", "Bracken & Paddy", "A, B & C". */
export function dogNames(b: Pick<BookingDetail, 'dogs'>): string {
  const names = (b.dogs ?? []).map((d) => d.name).filter(Boolean);
  if (names.length === 0) return 'the dogs';
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

// NO SLOT ON THE SELF-SERVICE CARD (Freddi, 2026-10-11: "we don't need any
// message to appear in the collapsed state of the self service card as we're
// doubling up with the deposit card"): the Money card's slot carries the
// words for the booking's money, which is what there is to say.

export interface MessageSlotProps {
  first: string;
  phone: string | null | undefined;
  text: string;
  /**
   * The text still has a sign-up link to make (messages-one-shape AC2a): the
   * link is made when Patricia taps WhatsApp, never when the slot draws, and
   * this returns the words with it in — or null if it could not be made.
   */
  prepare?: () => Promise<string | null>;
  /** Always open, no fold (App access; Freddi, 2026-10-14: "remove the expand/collapse button for the message"). */
  fixed?: boolean;
}

/**
 * THE TAP THAT MAKES THE LINK. The window opens first, in the tap itself, so a
 * phone's popup rule lets it through; the link is made while it loads; then it
 * is pointed at WhatsApp with the real words. A link that could not be made
 * closes it, and nothing is sent with a placeholder in it.
 */
export async function sendWithLink(args: { open: typeof window.open; phone: string | null | undefined; prepare: () => Promise<string | null> }): Promise<void> {
  const w = args.open('', '_blank');
  // A blocked pop-up makes nothing (PR #267 review): no link, no Link sent, for a window never shown.
  if (!w) return;
  // The tab is still our blank page: cut its way back to the desk before WhatsApp loads in it, as the
  // plain WhatsApp link's noopener does.
  try { w.opener = null; } catch { /* already cross-origin */ }
  const words = await args.prepare().catch(() => null);
  const wa = words ? whatsappTo(args.phone, words) : null;
  if (!wa) { w.close(); return; }
  w.location.href = wa;
}

/** Tucked by default (Freddi, 2026-10-11: "we will just copy the message most of the time … i like the idea of being able to tuck it away"). */
export function MessageSlot(props: MessageSlotProps) {
  const [open, setOpen] = useState(false);
  return <MessageSlotView {...props} open={open} onToggle={() => setOpen((o) => !o)} />;
}

// ONE ROW, ONE DOOR (Freddi, 2026-10-11). The row is the disclosure — the
// label, the first line of the words trailing off, the chevron — and at its
// right, never moving, WhatsApp's green square with the mark alone, opening
// the thread with the words in it. Unfolded, the words take the whole width
// beneath the row — and that is all: no Copy (Freddi, 2026-10-11: "the copy
// message button should be removed we should only have the whatsapp button").
export function MessageSlotView({ first, phone, text, prepare, fixed = false, open: toggled, onToggle }: MessageSlotProps & { open: boolean; onToggle: () => void }) {
  const wa = whatsappTo(phone, text);
  const open = fixed || toggled;
  return (
    <div className={`ss-msg${open ? ' open' : ''}${fixed ? ' fixed' : ''}`}>
      <div className="ss-msg-row">
        {fixed ? <div className="ss-msg-hd"><span className="ss-msg-eyebrow">Message for {first}</span></div> : <button type="button" className="ss-msg-hd" aria-expanded={open} onClick={onToggle}>
          <span className="ss-msg-eyebrow">Message for {first}</span>
          {open ? <span className="ss-msg-preview is-hint">Tap to tuck away</span> : <span className="ss-msg-preview">{text}</span>}
          <Icon name="chevron-down" size={14} className="ss-msg-chev" />
        </button>}
        <div className="ss-msg-acts">
          {wa && prepare
            ? <button type="button" className="ss-msg-wa" aria-label={`Open WhatsApp to ${first} — makes their sign-up link`} onClick={() => void sendWithLink({ open: window.open.bind(window), phone, prepare })}><Icon name="whatsapp" size={13} /></button>
            : wa
            ? <a className="ss-msg-wa" href={wa} target="_blank" rel="noreferrer noopener" aria-label={`Open WhatsApp with ${first}`} title={`Open WhatsApp with ${first}`}><Icon name="whatsapp" size={13} /></a>
            : <span className="ss-msg-wa is-off" title="No number on file"><Icon name="whatsapp" size={13} /></span>}
        </div>
      </div>
      {fixed ? <div className="ss-msg-body"><p>{text}</p></div> : <DisclosureRegion open={open}><div className="ss-msg-body"><p>{text}</p></div></DisclosureRegion>}
    </div>
  );
}
